import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Breakpoint,
	Card,
	GraphQLErrorMessage,
	Heading3,
	ITimeMachine,
	Paragraph,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { AnalyticsGraphMetric } from './metrics/AnalyticsGraphMetric';
import { AnalyticsMetric, AnalyticsMetricContainer } from './metrics/AnalyticsMetric';
import { TrafficLight } from './metrics/TrafficLight';
import { AnalyticsTreamsScoreOverviewCard } from './treams-score/AnalyticsTreamsScoreOverviewCard';

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: ${tokens.gap.group}px;

	@media screen and (max-width: 1200px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media screen and (max-width: ${Breakpoint.Small}) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

/** Same box as the loaded metric link, so swapping in the value does not grow the row. */
const DeferredMetricSlot = styled(Box)`
	display: block;
	margin: 0 -9px;
	padding: 4px 9px;
`;

const DEFERRED_METRIC_IDS = new Set(['goals.onTime', 'retention.loginPercentage']);

function DeferredPercentageMetric(props: {
	metricGroupID: string;
	metric?: graphql.AnalyticsMetric | null;
	labelKey: string;
	totalUsers: number;
	timeMachine: ITimeMachine;
	userFilter?: graphql.AnalyticsUserFilter;
}) {
	if (props.metric) {
		return (
			<AnalyticsMetricContainer>
				<AnalyticsMetric
					metricGroupID={props.metricGroupID}
					metric={props.metric}
					totalUsers={props.totalUsers}
					timeMachine={props.timeMachine}
					userFilter={props.userFilter}
				/>
			</AnalyticsMetricContainer>
		);
	}

	return <DeferredPercentageSkeleton labelKey={props.labelKey} />;
}

function renderGroupMetrics(props: {
	metricGroup: { id: string; metrics: readonly { id: string }[] };
	totalUsers: number;
	timeMachine: ITimeMachine;
	userFilter?: graphql.AnalyticsUserFilter;
	deferred: {
		login?: graphql.AnalyticsMetric | null;
		onTime?: graphql.AnalyticsMetric | null;
	};
}) {
	const metrics = props.metricGroup.metrics.filter((metric) => !DEFERRED_METRIC_IDS.has(metric.id));
	const rows = metrics.map((metric) => {
		const isGraph = (metric as graphql.AnalyticsMetric).__typename === 'AnalyticsGraphMetric';
		return (
			<AnalyticsMetricContainer key={metric.id} $noBorder={isGraph} mb={isGraph ? 3 : 0}>
				<AnalyticsMetric
					metricGroupID={props.metricGroup.id}
					metric={metric as graphql.AnalyticsMetric}
					totalUsers={props.totalUsers}
					timeMachine={props.timeMachine}
					userFilter={props.userFilter}
				/>
			</AnalyticsMetricContainer>
		);
	});

	if (props.metricGroup.id === 'goals') {
		const afterTotal = metrics.findIndex((metric) => metric.id === 'goals.total');
		rows.splice(
			afterTotal === -1 ? rows.length : afterTotal + 1,
			0,
			<DeferredPercentageMetric
				key="deferred:goals.onTime"
				metricGroupID="goals"
				metric={props.deferred.onTime as graphql.AnalyticsMetric | undefined}
				labelKey="analytics.metrics.goals.onTime"
				totalUsers={props.totalUsers}
				timeMachine={props.timeMachine}
				userFilter={props.userFilter}
			/>,
		);
	}

	if (props.metricGroup.id === 'retention') {
		rows.push(
			<DeferredPercentageMetric
				key="deferred:retention.loginPercentage"
				metricGroupID="retention"
				metric={props.deferred.login as graphql.AnalyticsMetric | undefined}
				labelKey="analytics.metrics.retention.loginPercentage"
				totalUsers={props.totalUsers}
				timeMachine={props.timeMachine}
				userFilter={props.userFilter}
			/>,
		);
	}

	return rows;
}

function DeferredPercentageSkeleton(props: { labelKey: string }) {
	const { t } = useTranslation();

	return (
		<AnalyticsMetricContainer>
			<DeferredMetricSlot>
				<Box py={1}>
					<Flex alignItems="center" justifyContent="space-between">
						<Box>
							<Paragraph>
								<strong>
									<Skeleton.Box
										width="2.25em"
										height="0.7em"
										style={{
											display: 'inline-block',
											verticalAlign: '0.1em',
											borderRadius: 4,
										}}
									/>{' '}
								</strong>
								{t(props.labelKey)}
							</Paragraph>
						</Box>
						<Flex alignItems="center" flexShrink={0}>
							<TrafficLight.Element ml={2} signal="good" />
							<Box
								ml={`${tokens.gap.element}px`}
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									color: tokens.icon.secondary,
								}}
							>
								<ChevronRightIcon size={18} color="currentColor" />
							</Box>
						</Flex>
					</Flex>
				</Box>
			</DeferredMetricSlot>
		</AnalyticsMetricContainer>
	);
}

export function AnalyticsGroupMetricSkeleton() {
	return (
		<Skeleton.Repeat count={3}>
			<Card.Skeleton>
				<Heading3.Skeleton mb={3} />

				<AnalyticsGraphMetric.Skeleton mb={3} />

				<Skeleton.Repeat count={3}>
					<Paragraph.Skeleton lines={1} mt={2} />
				</Skeleton.Repeat>
			</Card.Skeleton>
		</Skeleton.Repeat>
	);
}

export function AnalyticsMetrics(props: {
	timeMachine: ITimeMachine;
	groups: graphql.Group[];
	hasExplicitGroupSelection?: boolean;
	userFilter?: graphql.AnalyticsUserFilter;
	engagementOfficerMetricsOnly: boolean;
}) {
	const { t } = useTranslation();
	const userRoles = useViewerRoles();
	const hasTreamsScore = useFeature(graphql.FeatureName.TreamsScore);
	const showTreamsScoreCard = hasTreamsScore && !props.engagementOfficerMetricsOnly;
	const variables = useMemo(
		() => ({
			type: graphql.AnalyticsType.Workspace,
			filters: {
				startDate: props.timeMachine.current[0],
				endDate: props.timeMachine.current[1],
				groups: props.groups.map((group) => _.pick(group, ['id', 'type'])),
				userFilter: props.userFilter,
				engagementOfficerMetricsOnly: props.engagementOfficerMetricsOnly,
			},
		}),
		[props.timeMachine, props.groups, props.userFilter, props.engagementOfficerMetricsOnly],
	);
	const { data, loading, error } = graphql.useAnalyticsQuery({
		variables,
	});
	const skipDeferred = !data || loading;
	const loginQuery = graphql.useAnalyticsMetricQuery({
		variables: {
			...variables,
			metricGroupID: 'retention',
			metricID: 'retention.loginPercentage',
		},
		skip: skipDeferred,
		fetchPolicy: 'no-cache',
	});
	const onTimeQuery = graphql.useAnalyticsMetricQuery({
		variables: {
			...variables,
			metricGroupID: 'goals',
			metricID: 'goals.onTime',
		},
		skip: skipDeferred,
		fetchPolicy: 'no-cache',
	});

	if (!loading && data) {
		const loginMetric = loginQuery.data?.analytics.metricGroup?.metric;
		const onTimeMetric = onTimeQuery.data?.analytics.metricGroup?.metric;

		return (
			<Grid>
				{showTreamsScoreCard && (
					<AnalyticsTreamsScoreOverviewCard
						timeMachine={props.timeMachine}
						groups={props.groups}
						hasExplicitGroupSelection={Boolean(props.hasExplicitGroupSelection)}
					/>
				)}
				{data.analytics.metricGroups
					.filter((metricGroup) => {
						// When user is engagement officer, engagement metric should only be visible when listing eo-metrics.
						// This prevents user confusion, as these metrics are accessible in the 'team performance' tab as well.
						if (
							userRoles.isEngagementOfficer &&
							!props.engagementOfficerMetricsOnly &&
							metricGroup.id === 'engagement'
						) {
							return false;
						}
						return true;
					})
					.map((metricGroup) => (
						<Card key={metricGroup.id}>
							<Heading3 mb={3}>{t(`analytics.metrics.groupTitle.${metricGroup.id}`)}</Heading3>

							{renderGroupMetrics({
								metricGroup,
								totalUsers: data.analytics.totalUsers,
								timeMachine: props.timeMachine,
								userFilter: props.userFilter,
								deferred: {
									login: loginMetric as graphql.AnalyticsMetric | undefined,
									onTime: onTimeMetric as graphql.AnalyticsMetric | undefined,
								},
							})}
						</Card>
					))}
			</Grid>
		);
	}

	if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	return (
		<Grid>
			{showTreamsScoreCard && (
				<AnalyticsTreamsScoreOverviewCard
					timeMachine={props.timeMachine}
					groups={props.groups}
					hasExplicitGroupSelection={Boolean(props.hasExplicitGroupSelection)}
				/>
			)}
			<AnalyticsGroupMetricSkeleton />
		</Grid>
	);
}
